import React, { useContext, useCallback, useEffect } from "react";
import * as yup from "yup";
import _ from "lodash";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import {
  trainFiltersReset,
  trainSearchFormReset,
  trainSearchFormUpdate,
  trainSearchResultsReset,
} from "../../../actions/search/trains_search";
import { useTrainsInitSearch } from "../../../hooks/trains";
import { TranslationContext } from "../../../umbilical_tools";
import { useDispatch, useSelector } from "react-redux";
import { NoScrollContext } from "../../../context";
export const schema = yup.object().shape({
  adults: yup
    .number()
    .integer()
    .min(0)
    .test(
      "at-least-one",
      "You haven't select any passengers",
      function (value) {
        const seniors = this.parent.seniors || 0;
        const youths = this.parent.youths || 0;
        return value > 0 || seniors > 0 || youths > 0;
      }
    ),
  adults_ages: yup.array().of(yup.number().required().integer().min(30)),
  seniors: yup.number().required().integer().min(0),
  seniors_ages: yup.array().of(yup.number().required().integer().min(60)),
  youths: yup.number().required().integer().min(0),
  youths_ages: yup.array().of(yup.number().required().integer().min(0)),
  departDateTime: yup.array().of(yup.string().required()),
  returnDateTime: yup
    .array()
    .test(
      "is-required",
      "Return date must be after departure date",
      function (value) {
        const hasReturn = this.parent.hasReturn || false;
        const departDateTime = this.parent.departDateTime || {};
        const returnDate = DateTime.fromISO(value);
        const departDate = DateTime.fromISO(departDateTime[0]);
        return hasReturn ? value && returnDate >= departDate : true;
      }
    ),

  hasReturn: yup.boolean().required(),
  origin: yup
    .object()
    .test("is-required", "Please select a departure station", function (value) {
      return value && Object.keys(value.origin_meta).length > 0;
    }),
  destination: yup
    .object()
    .test(
      "is-required",
      "Please select a destination station",
      function (value) {
        return value && Object.keys(value.destination_meta).length > 0;
      }
    )
    .test(
      "is-different",
      "Origin and destination must be different",
      function (value) {
        const origin = this.parent.origin || {};
        const originCode = _.get(origin, "origin_meta.0.code", "");
        const destinationCode = _.get(value, "destination_meta.0.code", "");

        return originCode && destinationCode
          ? originCode !== destinationCode
          : true;
      }
    ),
});

export async function validateForm(form) {
  const errors = await schema

    .validate(form, { abortEarly: false })
    .then(() => [])
    .catch((errors) => {
      if (errors.inner) {
        const error_msgs = errors.inner.map((err) => err.message);
        toast.error(
          <ul>
            {error_msgs.map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        );
      }
      return errors.inner;
    });

  return errors.length > 0;
}

export function useTrainSearchFormHandle({
  setShowRailPaxPickerModal,
  setLoadingSearchBtn,
  isNewSearch,
}) {
  const { initSearch } = useTrainsInitSearch({ setLoadingSearchBtn });

  const { t } = useContext(TranslationContext);
  const dispatch = useDispatch();
  const { toggleNoScroll } = useContext(NoScrollContext);

  const search_form = useSelector((state) => state.TrainSearchForm);

  const onFormUpdate = useCallback(
    ({ form }) => dispatch(trainSearchFormUpdate({ form })),
    [dispatch]
  );

  const onClearState = useCallback(() => {
    dispatch(trainSearchFormReset());
    dispatch(trainSearchResultsReset());
    dispatch(trainFiltersReset());
  }, [dispatch]);

  function onClickPax() {
    setShowRailPaxPickerModal(true);
  }

  async function onInitSearch(values) {
    var form = {
      origin: values.origin.origin_meta[0],
      destination: values.destination.destination_meta[0],
      departDateTime: DateTime.fromISO(values.departDateTime[0]).toFormat(
        "yyyy-MM-dd'T'HH:mm:ss"
      ),
      returnDateTime: DateTime.fromISO(values.returnDateTime[0]).toFormat(
        "yyyy-MM-dd'T'HH:mm:ss"
      ),
      hasReturn: values.hasReturn,
    };

    await onFormUpdate({
      form,
    });

    initSearch(form);
  }

  useEffect(() => {
    if (!isNewSearch) {
      onClearState();
    }
  }, []);

  return {
    search_form,
    onInitSearch,
    onClickPax,
    t,
  };
}
